import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { BypassRequestStreamService, BypassRequestWatch } from 'src/app/services/bypass-request-stream/bypass-request-stream.service';
import { DataService } from 'src/app/services/data/data.service';
import {
  AppConfig,
  HiddenMenuMode,
  PinDialogEvent,
  PinDialogVerifyResult,
  StaffPinDialogState,
  StaffPinErrorCode,
  StaffPinText,
} from 'src/app/shared/constants/global-config.constant';
import {
  IApiErrorBody,
  IBypassReason,
  IBypassRequest,
  IStaffPinCheck,
  StaffMenuAction,
} from 'src/app/shared/interfaces/interface';

/** Lockout lasts 5 minutes (D26); a countdown is never shown longer than that, whatever the booth clock says. */
const MAX_LOCK_MS = 5 * 60 * 1000;
/** An approval request lives 5 minutes (D10). */
const DEFAULT_REQUEST_MS = 5 * 60 * 1000;

/**
 * The staff PIN dialog, in two modes (D15, D27). Contract: sc-screenlock-v2-api
 * docs/staff-pin-bypass-contract.md §2 and §5.
 *
 * - **Payment bypass** (`pinMode` NONE, on /payment-or-promo): keypad, checked once at 6 digits
 *   by `POST /bypass/pin/check`; greeting with today's count; reason buttons. Within the limit
 *   a reason calls `POST /bypass`. Over it the button reads "ส่งขออนุมัติ" and calls
 *   `POST /bypass/requests`, then waits for the approver over SSE (polling as a fallback),
 *   with a countdown and Cancel.
 * - **Staff menu** (`pinMode` CONFIG / EXIT / LOGOUT, on /home): the same keypad, then
 *   `POST /staff-menu`. No reason, no limit.
 *
 * On success it emits `onSuccessVerify`; the host keeps doing what it did before (set
 * IS_PIN_USE and go to /payment-success, or run the staff-menu action).
 *
 * A pending approval request is cancelled on Cancel, on the X, and on destroy (D35).
 *
 * While the lockout countdown is on screen (either mode), the dialog asks the API every
 * `AppConfig.STAFF_PIN_LOCKOUT_CHECK_MS` whether the back office has lifted it (D53).
 */
@Component({
  selector: 'app-staff-pin-dialog',
  templateUrl: './staff-pin-dialog.component.html',
  styleUrls: ['./staff-pin-dialog.component.scss']
})
export class StaffPinDialogComponent implements OnInit, OnDestroy {

  constructor(
    private dataService: DataService,
    private streamService: BypassRequestStreamService,
    private ngZone: NgZone,
    private host: ElementRef<HTMLElement>,
  ) { }

  /** NONE = payment bypass; CONFIG / EXIT / LOGOUT = staff-menu entry. */
  @Input() pinMode: HiddenMenuMode = HiddenMenuMode.NONE;
  @Output() onSuccessVerify: EventEmitter<PinDialogVerifyResult> = new EventEmitter<PinDialogVerifyResult>();
  @Output() onCloseDialog: EventEmitter<PinDialogVerifyResult> = new EventEmitter<PinDialogVerifyResult>();

  readonly TXT = StaffPinText;
  readonly State = StaffPinDialogState;
  readonly digitKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
  readonly dots = Array.from({ length: AppConfig.STAFF_PIN_LENGTH }, (_, i) => i);

  state: StaffPinDialogState = StaffPinDialogState.PIN;
  typed = '';
  /** A request is in flight; the keypad and reason buttons wait. */
  busy = false;
  /**
   * POST /bypass or POST /staff-menu is in flight. The X is hidden meanwhile: closing then would
   * record the PIN use and never unlock the photobooth (or run the staff action).
   */
  granting = false;
  /** One line of feedback under the dots or above the reasons. */
  message = '';
  isOffline = false;

  check: IStaffPinCheck | null = null;
  needsApproval = false;
  reasons: IBypassReason[] = [];
  reasonsLoading = false;
  reasonsFailed = false;
  /** The reason whose button was tapped, while its request is in flight. */
  activeReasonId: number | null = null;
  request: IBypassRequest | null = null;

  /** Seconds left on the approval countdown or the lockout. */
  secondsLeft = 0;

  /** The PIN that passed the check, kept only until the reason is sent (the API re-checks it). */
  private heldPin: string | null = null;
  private destroyed = false;
  private watch: BypassRequestWatch | null = null;
  private countdownTimer: any = null;
  private countdownDeadline = 0;
  private expiryGraceTimer: any = null;
  private approvedTimer: any = null;
  private reasonsIdleTimer: any = null;
  private lockoutCheckTimer: any = null;
  private lockoutCheck: Subscription | null = null;
  /**
   * The lockout countdown matches the server's locked_until. False when the booth clock was so far
   * off that the countdown had to fall back to the full 5 minutes: it then says nothing about how
   * long the lockout really had left.
   */
  private lockoutCountdownTrusted = false;

  get isBypass(): boolean {
    return !this.pinMode || this.pinMode === HiddenMenuMode.NONE;
  }

  get title(): string {
    switch (this.pinMode) {
      case HiddenMenuMode.CONFIG: return StaffPinText.TITLE_CONFIG;
      case HiddenMenuMode.EXIT: return StaffPinText.TITLE_EXIT;
      case HiddenMenuMode.LOGOUT: return StaffPinText.TITLE_LOGOUT;
      default: return StaffPinText.TITLE_BYPASS;
    }
  }

  /** "สาขานี้ เหลือ 1 ครั้งวันนี้ (ใช้ไป 2/3)" (D18), or null when no limit applies. */
  get countText(): string | null {
    const c = this.check;
    if (!c || c.limit === null || c.limit === undefined || c.remaining === null || c.remaining === undefined) {
      return null;
    }
    return `สาขานี้ เหลือ ${c.remaining} ครั้งวันนี้ (ใช้ไป ${c.used_today ?? 0}/${c.limit})`;
  }

  get countdownText(): string {
    const s = Math.max(0, this.secondsLeft);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  ngOnInit(): void {
    this.isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (this.isOffline) {
      this.state = StaffPinDialogState.OFFLINE;
    }
    if (this.isBypass) {
      this.loadReasons();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.state === StaffPinDialogState.PENDING && this.request) {
      this.dataService.cancelBypassRequest(this.request.id);
    }
    this.finishWaiting();
    this.stopCountdown();
    if (this.approvedTimer) {
      clearTimeout(this.approvedTimer);
      this.approvedTimer = null;
    }
    this.stopReasonsIdle();
    this.stopLockoutCheck();
    this.heldPin = null;
    this.typed = '';
  }

  /** Any tap on the dialog restarts the reason screen's idle timeout. */
  @HostListener('click')
  onInteraction() {
    if (this.state === StaffPinDialogState.REASONS) {
      this.armReasonsIdle();
    }
  }

  /**
   * A physical keyboard on the keypad screen (2.17.1), for HQ typing a PIN over AnyDesk: a mouse
   * click on the on-screen keypad would show the digits to whoever stands at the photobooth.
   * Digits (top row or numpad) type, Backspace deletes one, Delete clears. Nothing on screen
   * lights up, only the dots fill. Space and Enter are swallowed, so a keypad button that kept
   * focus after a mouse click can't type its digit again. Every other screen ignores the keyboard.
   */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (this.state !== StaffPinDialogState.PIN || event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    const digit = /^[0-9]$/.test(event.key)
      ? event.key
      : /^Numpad[0-9]$/.test(event.code) ? event.code.slice(-1) : null;
    if (digit !== null) {
      event.preventDefault();
      this.blurKeypadButton();
      if (!event.repeat) {
        this.addDigit(digit);
      }
    }
    else if (event.key === 'Backspace') {
      event.preventDefault();
      this.deleteDigit();
    }
    else if (event.key === 'Delete') {
      event.preventDefault();
      this.clearPin();
    }
    else if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
      event.preventDefault();
    }
  }

  /** A keypad button focused by an earlier click would otherwise stay highlighted while typing. */
  private blurKeypadButton() {
    const active = document.activeElement as HTMLElement | null;
    if (active && active !== document.body && this.host.nativeElement.contains(active)) {
      active.blur();
    }
  }

  // ---- Keypad ----

  addDigit(digit: string) {
    if (this.busy || this.state !== StaffPinDialogState.PIN || this.typed.length >= AppConfig.STAFF_PIN_LENGTH) {
      return;
    }
    this.message = '';
    this.typed += digit;
    if (this.typed.length === AppConfig.STAFF_PIN_LENGTH) {
      this.submitPin();
    }
  }

  deleteDigit() {
    if (!this.busy) {
      this.typed = this.typed.slice(0, -1);
    }
  }

  clearPin() {
    if (!this.busy) {
      this.typed = '';
    }
  }

  /** Runs once, when the 6th digit is typed. Never per key (D24). */
  private submitPin() {
    if (this.isOffline) {
      this.showOffline();
      return;
    }
    const pin = this.typed;
    this.busy = true;
    if (this.isBypass) {
      this.dataService.checkStaffPin(pin, 'PAYMENT_BYPASS').subscribe({
        next: response => {
          if (this.destroyed) {
            return;
          }
          this.busy = false;
          this.heldPin = pin;
          this.typed = '';
          this.check = response.data;
          this.needsApproval = !!response.data?.needs_approval;
          this.state = StaffPinDialogState.REASONS;
          this.armReasonsIdle();
          if (this.reasons.length === 0 && !this.reasonsLoading) {
            this.loadReasons();
          }
        },
        error: err => this.handleError(err),
      });
    }
    else {
      this.granting = true;
      this.dataService.useStaffMenu(pin, this.pinMode as StaffMenuAction).subscribe({
        next: response => {
          if (this.destroyed) {
            return;
          }
          this.busy = false;
          this.granting = false;
          this.succeed(response?.data?.pin_use_id ?? null);
        },
        error: err => this.handleError(err),
      });
    }
  }

  // ---- Reasons ----

  loadReasons() {
    this.reasonsLoading = true;
    this.reasonsFailed = false;
    this.dataService.getBypassReasons().subscribe({
      next: response => {
        this.reasonsLoading = false;
        this.reasons = response?.data ?? [];
        this.reasonsFailed = this.reasons.length === 0;
      },
      error: () => {
        this.reasonsLoading = false;
        this.reasonsFailed = true;
      },
    });
  }

  chooseReason(reason: IBypassReason) {
    if (this.busy || this.state !== StaffPinDialogState.REASONS || !this.heldPin) {
      return;
    }
    if (this.isOffline) {
      this.showOffline();
      return;
    }
    this.busy = true;
    this.activeReasonId = reason.id;
    this.message = '';
    if (!this.needsApproval) {
      this.granting = true;
      this.dataService.createBypass(this.heldPin, reason.id).subscribe({
        next: response => {
          if (this.destroyed) {
            return;
          }
          this.busy = false;
          this.granting = false;
          this.activeReasonId = null;
          this.succeed(response?.data?.pin_use_id ?? null);
        },
        error: err => this.handleError(err),
      });
    }
    else {
      this.dataService.createBypassRequest(this.heldPin, reason.id).subscribe({
        next: response => this.onRequestCreated(response?.data?.request),
        error: err => this.handleError(err),
      });
    }
  }

  // ---- Approval request ----

  private onRequestCreated(request: IBypassRequest | undefined) {
    if (this.destroyed) {
      // The dialog went away while the request was being created: nobody is waiting for it.
      if (request?.status === 'PENDING') {
        this.dataService.cancelBypassRequest(request.id);
      }
      return;
    }
    this.busy = false;
    this.activeReasonId = null;
    this.stopReasonsIdle();
    if (!request) {
      this.showError();
      return;
    }
    this.request = request;
    this.heldPin = null;
    if (request.status === 'NO_APPROVER') {
      this.state = StaffPinDialogState.NO_APPROVER;
      return;
    }
    if (request.status !== 'PENDING') {
      this.state = StaffPinDialogState.PENDING;
      this.applyRequestStatus(request);
      return;
    }
    this.state = StaffPinDialogState.PENDING;
    // The countdown runs on the server's own duration, anchored to now, so a booth clock that
    // is off by minutes cannot shorten or stretch it (D16: the API's clock decides).
    const created = Date.parse(request.created_at);
    const expires = Date.parse(request.expires_at);
    const duration = expires > created ? Math.min(expires - created, DEFAULT_REQUEST_MS * 2) : DEFAULT_REQUEST_MS;
    this.startCountdown(duration, () => this.onRequestCountdownEnded());
    this.watch = this.streamService.watch(request.id, {
      // Both callbacks arrive outside the Angular zone (the stream runs there). Without
      // ngZone.run nothing is redrawn and the approval never reaches the screen (2.16.1).
      onStatus: updated => this.ngZone.run(() => this.applyRequestStatus(updated)),
      onError: () => this.ngZone.run(() => this.onWatchFailed()),
    });
  }

  /** Every status the server reports for the current request, from the stream, a poll or a check. */
  applyRequestStatus(request: IBypassRequest) {
    if (this.destroyed || this.state !== StaffPinDialogState.PENDING || !this.request || request.id !== this.request.id) {
      return;
    }
    this.request = request;
    switch (request.status) {
      case 'PENDING':
        return;
      case 'APPROVED':
        this.finishWaiting();
        // "อนุมัติแล้ว" (and who approved, and the note) stays up briefly, then the photobooth
        // moves on. Destroy clears the timer, so leaving during it never navigates twice.
        this.state = StaffPinDialogState.APPROVED;
        this.approvedTimer = setTimeout(() => {
          this.approvedTimer = null;
          if (!this.destroyed && this.state === StaffPinDialogState.APPROVED) {
            this.succeed(request.pin_use_id);
          }
        }, AppConfig.BYPASS_APPROVED_SHOW_MS);
        return;
      case 'REJECTED':
        this.finishWaiting();
        this.state = StaffPinDialogState.REJECTED;
        return;
      case 'EXPIRED':
        this.finishWaiting();
        this.state = StaffPinDialogState.EXPIRED;
        return;
      case 'CANCELLED':
        this.finishWaiting();
        this.state = StaffPinDialogState.CANCELLED;
        return;
      case 'NO_APPROVER':
        this.finishWaiting();
        this.state = StaffPinDialogState.NO_APPROVER;
        return;
    }
  }

  /** Cancel while waiting (D35): closes the request on the server and closes the dialog. */
  cancelRequest() {
    if (this.state !== StaffPinDialogState.PENDING || !this.request) {
      return;
    }
    this.closeDialog();
  }

  /**
   * The countdown reached 0. The server expires the request on its own timer and on every read,
   * so one read normally brings EXPIRED. If nothing arrives within the grace period (no network),
   * the dialog says "หมดเวลา" itself and cancels, so the request can never be approved late.
   */
  private onRequestCountdownEnded() {
    const request = this.request;
    if (!request || this.state !== StaffPinDialogState.PENDING) {
      return;
    }
    // Armed before the read, so an answer (which clears it) can never leave it running.
    this.expiryGraceTimer = setTimeout(() => {
      this.expiryGraceTimer = null;
      if (this.state === StaffPinDialogState.PENDING && this.request?.id === request.id) {
        this.finishWaiting();
        this.dataService.cancelBypassRequest(request.id);
        this.state = StaffPinDialogState.EXPIRED;
      }
    }, AppConfig.BYPASS_EXPIRY_GRACE_MS);
    this.dataService.getBypassRequest(request.id).subscribe({
      next: response => {
        const updated = response?.data?.request;
        if (updated) {
          this.ngZone.run(() => this.applyRequestStatus(updated));
        }
      },
      error: () => { },
    });
  }

  private onWatchFailed() {
    if (this.state !== StaffPinDialogState.PENDING) {
      return;
    }
    const id = this.request?.id;
    this.finishWaiting();
    // Nobody can follow it any more, so nobody should be able to approve it either.
    if (id !== undefined) {
      this.dataService.cancelBypassRequest(id);
    }
    this.showError();
  }

  /** Stops the stream, the countdown and the expiry grace timer. */
  private finishWaiting() {
    if (this.watch) {
      this.watch.stop();
      this.watch = null;
    }
    if (this.expiryGraceTimer) {
      clearTimeout(this.expiryGraceTimer);
      this.expiryGraceTimer = null;
    }
    if (this.state === StaffPinDialogState.PENDING) {
      this.stopCountdown();
    }
  }

  // ---- Outcomes ----

  private succeed(pinUseId: number | null) {
    this.stopReasonsIdle();
    this.state = StaffPinDialogState.SUCCESS;
    this.heldPin = null;
    this.typed = '';
    this.onSuccessVerify.emit({
      mode: this.pinMode ?? HiddenMenuMode.NONE,
      event: PinDialogEvent.SUCCESS,
      pin_use_id: pinUseId,
    });
  }

  private handleError(err: any) {
    if (this.destroyed) {
      return;
    }
    this.busy = false;
    this.granting = false;
    this.activeReasonId = null;
    this.typed = '';
    const body: IApiErrorBody | undefined = err?.error && typeof err.error === 'object' ? err.error : undefined;
    const code = body?.code;

    if (err?.status === 0 || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      this.showOffline();
      return;
    }

    // The 5th failure comes back as WRONG_PIN / NOT_ALLOWED_BRANCH with attempts_left 0 and
    // locked_until: lock now, rather than show "0 left" and lock on the next try.
    if ((code === StaffPinErrorCode.WRONG_PIN || code === StaffPinErrorCode.NOT_ALLOWED_BRANCH) && body?.data?.locked_until) {
      this.showLocked(body.data.locked_until);
      return;
    }

    switch (code) {
      case StaffPinErrorCode.WRONG_PIN:
      case StaffPinErrorCode.INVALID_PIN_FORMAT:
        this.backToKeypad(this.withAttempts(StaffPinText.WRONG_PIN, body?.data?.attempts_left));
        return;
      case StaffPinErrorCode.NOT_ALLOWED_BRANCH:
        this.backToKeypad(this.withAttempts(StaffPinText.NOT_ALLOWED_BRANCH, body?.data?.attempts_left));
        return;
      case StaffPinErrorCode.LOCKED:
        this.showLocked(body?.data?.locked_until);
        return;
      case StaffPinErrorCode.NEEDS_APPROVAL:
        // Someone used up the limit between the check and the tap: the same reason now needs approval.
        this.needsApproval = true;
        if (this.check && body?.data?.limit !== undefined) {
          const used = body.data.used_today ?? this.check.used_today ?? 0;
          this.check = { ...this.check, used_today: used, limit: body.data.limit, remaining: Math.max(body.data.limit - used, 0) };
        }
        this.state = StaffPinDialogState.REASONS;
        this.armReasonsIdle();
        return;
      case StaffPinErrorCode.APPROVAL_NOT_NEEDED:
        this.needsApproval = false;
        this.state = StaffPinDialogState.REASONS;
        this.message = StaffPinText.APPROVAL_NOT_NEEDED;
        this.armReasonsIdle();
        return;
      case StaffPinErrorCode.INVALID_REASON:
        this.state = StaffPinDialogState.REASONS;
        this.message = StaffPinText.INVALID_REASON;
        this.armReasonsIdle();
        this.loadReasons();
        return;
      default:
        this.showError();
    }
  }

  private withAttempts(text: string, attemptsLeft: number | undefined): string {
    return attemptsLeft === undefined || attemptsLeft === null
      ? text
      : `${text} (${StaffPinText.ATTEMPTS_LEFT} ${attemptsLeft} ครั้ง)`;
  }

  private backToKeypad(message: string) {
    this.resetToPin();
    this.message = message;
  }

  private showLocked(lockedUntil: string | undefined) {
    this.resetToPin();
    const until = lockedUntil ? Date.parse(lockedUntil) : NaN;
    // A fresh lock is always a real lock (the server enforces it): a booth clock running fast
    // must not turn it into a 0:00 countdown, so anything not in the future counts as the full 5 minutes.
    let left = isNaN(until) ? MAX_LOCK_MS : until - Date.now();
    // Trusted = the booth clock roughly agrees with the server's. Up to a minute over 5:00 is a
    // booth clock running a little behind (the server's locked_until is its own now + 5 min).
    this.lockoutCountdownTrusted = !isNaN(until) && left > 0 && left <= MAX_LOCK_MS + AppConfig.STAFF_PIN_LOCKOUT_CLOCK_SLACK_MS;
    if (left <= 0) {
      left = MAX_LOCK_MS;
    }
    left = Math.min(left, MAX_LOCK_MS);
    this.state = StaffPinDialogState.LOCKED;
    this.startCountdown(left, () => {
      if (this.state === StaffPinDialogState.LOCKED) {
        this.resetToPin();
      }
    });
    this.startLockoutCheck();
  }

  private showOffline() {
    this.stopReasonsIdle();
    this.busy = false;
    this.granting = false;
    this.typed = '';
    this.heldPin = null;
    this.check = null;
    this.state = StaffPinDialogState.OFFLINE;
  }

  private showError() {
    this.stopReasonsIdle();
    this.heldPin = null;
    this.state = StaffPinDialogState.ERROR;
  }

  /** Back to an empty keypad; everything from the last PIN is forgotten. */
  resetToPin() {
    this.stopCountdown();
    this.stopReasonsIdle();
    this.stopLockoutCheck();
    this.granting = false;
    this.state = this.isOffline ? StaffPinDialogState.OFFLINE : StaffPinDialogState.PIN;
    this.typed = '';
    this.heldPin = null;
    this.check = null;
    this.needsApproval = false;
    this.request = null;
    this.message = '';
    this.busy = false;
    this.activeReasonId = null;
  }

  closeDialog() {
    if (this.state === StaffPinDialogState.PENDING && this.request) {
      const id = this.request.id;
      this.finishWaiting();
      this.dataService.cancelBypassRequest(id);
      this.request = null;
      this.state = StaffPinDialogState.CANCELLED;
    }
    this.onCloseDialog.emit({
      mode: this.pinMode ?? HiddenMenuMode.NONE,
      event: PinDialogEvent.CLOSE_DIALOG,
    });
  }

  // ---- Network ----

  setOffline() {
    this.isOffline = true;
    // No bypass and no staff-menu entry while offline (D23). A pending request keeps waiting:
    // the stream reconnects or polls, and the answer shows once the network is back.
    if (this.state === StaffPinDialogState.PIN || this.state === StaffPinDialogState.REASONS) {
      this.showOffline();
    }
  }

  setOnline() {
    this.isOffline = false;
    if (this.state === StaffPinDialogState.OFFLINE) {
      // A lift that arrived during a network flap (or just before it) is still news when the
      // keypad comes back. Any other message, or a digit typed since (addDigit clears the
      // message), means it has already been replaced.
      const keep = this.message === StaffPinText.LOCKOUT_LIFTED ? this.message : '';
      this.resetToPin();
      this.message = keep;
    }
  }

  // ---- Reason screen idle timeout ----

  /** (Re)starts the reason screen's idle timeout; on expiry the checked PIN is forgotten. */
  private armReasonsIdle() {
    this.stopReasonsIdle();
    this.reasonsIdleTimer = setTimeout(() => {
      this.reasonsIdleTimer = null;
      if (this.state !== StaffPinDialogState.REASONS) {
        return;
      }
      if (this.busy) {
        // A reason was tapped and its request is still out; its answer decides what comes next.
        this.armReasonsIdle();
        return;
      }
      this.resetToPin();
      this.message = StaffPinText.REASONS_TIMEOUT;
    }, AppConfig.STAFF_PIN_REASONS_IDLE_MS);
  }

  private stopReasonsIdle() {
    if (this.reasonsIdleTimer) {
      clearTimeout(this.reasonsIdleTimer);
      this.reasonsIdleTimer = null;
    }
  }

  // ---- Lifting a PIN lockout (D53) ----

  /**
   * While LOCKED, asks every few seconds whether the back office has lifted the lockout. Never two
   * checks at once. Any error (offline, a 404 from an older API, a 5xx, a timeout) is ignored and
   * the countdown carries on; the next tick simply asks again. Every way out of LOCKED goes through
   * resetToPin() or ngOnDestroy(), which both stop it.
   */
  private startLockoutCheck() {
    this.stopLockoutCheck();
    this.lockoutCheckTimer = setInterval(() => this.checkLockout(), AppConfig.STAFF_PIN_LOCKOUT_CHECK_MS);
  }

  private checkLockout() {
    if (this.destroyed || this.state !== StaffPinDialogState.LOCKED) {
      this.stopLockoutCheck();
      return;
    }
    if (this.isOffline || (this.lockoutCheck && !this.lockoutCheck.closed)) {
      return;
    }
    this.lockoutCheck = this.dataService.getPinLockout().subscribe({
      // Wrapped like every other answer the dialog acts on, so the keypad is redrawn even if
      // the answer ever arrives outside the Angular zone (2.16.1).
      next: response => this.ngZone.run(() => {
        if (!this.destroyed && this.state === StaffPinDialogState.LOCKED && response?.data?.locked === false) {
          // locked: false also means the lockout simply ran out. Say "lifted" only when the
          // countdown is right and still had more than one check to go; otherwise this is a
          // natural expiry and the keypad comes back without a word, as it does at 0:00.
          const liftedEarly = this.lockoutCountdownTrusted
            && this.countdownDeadline - Date.now() > AppConfig.STAFF_PIN_LOCKOUT_CHECK_MS;
          this.resetToPin();
          if (liftedEarly) {
            this.message = StaffPinText.LOCKOUT_LIFTED;
          }
        }
      }),
      error: () => { },
    });
  }

  private stopLockoutCheck() {
    if (this.lockoutCheckTimer) {
      clearInterval(this.lockoutCheckTimer);
      this.lockoutCheckTimer = null;
    }
    if (this.lockoutCheck) {
      this.lockoutCheck.unsubscribe();
      this.lockoutCheck = null;
    }
  }

  // ---- Countdown ----

  private startCountdown(ms: number, onDone: () => void) {
    this.stopCountdown();
    this.countdownDeadline = Date.now() + ms;
    this.secondsLeft = Math.ceil(ms / 1000);
    this.countdownTimer = setInterval(() => {
      const left = this.countdownDeadline - Date.now();
      this.secondsLeft = Math.max(0, Math.ceil(left / 1000));
      if (left <= 0) {
        this.stopCountdown();
        onDone();
      }
    }, 1000);
  }

  private stopCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
